'use client';

import type { CSSProperties } from 'react';
import {
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { useDeeplinkedAccessRequestIdFromParams } from '@frameio/access-requests/hooks/useDeeplinkedAccessRequestIdFromParams';
import { EmptyDndContextProvider } from '@frameio/assets/operations';
import { ErrorBoundary } from '@frameio/shared/components';
import { AppSheetContext } from '@frameio/shared/contexts/AppSheetContext';
import { useDispatchAppState } from '@frameio/shared/contexts/AppStateContext';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { UpdateAppParams } from '@frameio/shared/routing/params/params';
import {
  isMobile,
  Sheet,
  theme,
  useActiveWindowBreakpoint,
  useOnStateChange,
} from '@frameio/vapor';
import { SettingsContextProvider as WorkspacesSettingsContext } from '@frameio/workspaces/settings';
import { PRIMARY_NAV_DURATION } from '../../constants';
import { useGetPrimaryNavBaseWidth } from '../../contexts/PrimaryNavContext';
import { DevToolsSheet } from './Sheets/DevToolsSheet';
import { NotificationSheet } from './Sheets/NotificationSheet';
import { ProjectNavigationSheet } from './Sheets/ProjectNavigationSheet';
import { ProjectSwitcherSheet } from './Sheets/ProjectSwitcherSheet/ProjectSwitcherSheet';
import { SettingsNavSheet } from './Sheets/SettingsNavSheet';
import { UploadsSheet } from './Sheets/UploadsSheet/UploadsSheet';
import { useActiveWhatsNewItem } from './Sheets/WhatsNewSheet/hooks/useActiveWhatsNewItem';
import { WhatsNewSheet } from './Sheets/WhatsNewSheet/WhatsNewSheet';
import { WorkspacesNavSheet } from './Sheets/WorkspacesNavigationSheet';

export const APP_SHEET_ID = 'app-sheet';

const OFFSET_CONTAINER_STYLES = {
  position: 'relative',
  gridArea: '3 / 1 / 6 / 3',
  // adding 2 px to the top to adjust for the loss of row-gap
  top: 2,
} as CSSProperties;

const NON_PROJECT_SHEET_STYLE = {
  zIndex: theme.zIndices.sheet + 1,
  ...OFFSET_CONTAINER_STYLES,
} as CSSProperties;

/**
 * The PrimaryNav sheet component. It should wrap any components
 * that need to trigger its appearance.
 *
 * @example
 * <AppSheet>
 *   <Header />
 *   <PrimaryNav />
 *   <Finder />
 * </AppSheet>
 */
export const AppSheet = () => {
  const { setId: setSheetId, id: sheetId } = useContext(AppSheetContext);
  const pathName = usePathname();
  const [lastPath, setLastPath] = useState(pathName);
  const dispatch = useDispatchAppState();
  const breakpoint = useActiveWindowBreakpoint();
  const isWebClient = useIsMatchingClientName('web');
  const [replaceState] = use(UpdateAppParams);
  const { activeWhatsNewItemId } = useActiveWhatsNewItem();
  const { deeplinkedAccessRequestId } =
    useDeeplinkedAccessRequestIdFromParams();
  const activeWhatsNewItemIdRef = useRef<string | undefined>(
    activeWhatsNewItemId
  );
  const deeplinkedAccessRequestIdRef = useRef<string | undefined>(
    deeplinkedAccessRequestId
  );

  const onRequestClose = useCallback(() => {
    if (sheetId === null) return;
    setSheetId?.(null);
    if (sheetId === 'whats-new') replaceState({ whats_new_item: null });
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
  }, [dispatch, sheetId, setSheetId, replaceState]);

  const isValidActiveWhatsNewItemId =
    activeWhatsNewItemId &&
    activeWhatsNewItemId !== 'invalid' &&
    !activeWhatsNewItemIdRef.current;
  useEffect(() => {
    if (isValidActiveWhatsNewItemId) {
      if (sheetId !== 'whats-new') {
        setSheetId?.('whats-new');
        /* prevents the sheet from staying open before the url param has reset */
        activeWhatsNewItemIdRef.current = activeWhatsNewItemId;
      }
    }
  }, [activeWhatsNewItemId, setSheetId, sheetId, isValidActiveWhatsNewItemId]);

  const isValidDeeplinkedAccessRequestId =
    deeplinkedAccessRequestId &&
    deeplinkedAccessRequestId !== 'invalid' &&
    !deeplinkedAccessRequestIdRef.current;

  useEffect(() => {
    if (isValidDeeplinkedAccessRequestId) {
      if (sheetId !== 'notifications') {
        setSheetId?.('notifications');
        /* prevents the sheet from staying open before the url param has reset */
        deeplinkedAccessRequestIdRef.current = deeplinkedAccessRequestId;
      }
    }
  }, [
    deeplinkedAccessRequestId,
    setSheetId,
    sheetId,
    isValidDeeplinkedAccessRequestId,
  ]);

  useEffect(() => {
    if (pathName !== lastPath) {
      setLastPath(pathName);
      onRequestClose();
    }
  }, [onRequestClose, lastPath, pathName]);

  /**
   * When switching to or from the `base` breakpoint, close any open sheets.
   */
  useOnStateChange(breakpoint, (prevBreakpoint, nextBreakpoint) => {
    if (sheetId && (prevBreakpoint === 'base' || nextBreakpoint === 'base')) {
      dispatch({ type: 'CLOSE_MOBILE_NAV' });
      setSheetId?.(null);
    }
  });

  const { contents, width } = useMemo(() => {
    switch (sheetId) {
      case 'project-switcher':
        return {
          contents: <ProjectSwitcherSheet />,
          width: 420,
        };

      case 'uploads':
        return {
          contents: <UploadsSheet />,
          width: 420,
        };
      case 'dev-tools':
        return {
          contents: <DevToolsSheet />,
          width: 420,
        };
      case 'notifications':
        return {
          contents: <NotificationSheet />,
          width: 420,
        };
      case 'whats-new':
        return {
          contents: <WhatsNewSheet />,
          width: 420,
        };
      case 'project-navigation':
        return {
          contents: (
            <EmptyDndContextProvider>
              <ProjectNavigationSheet />
            </EmptyDndContextProvider>
          ),
          width: 300,
        };
      case 'settings-navigation':
        return {
          contents: <SettingsNavSheet />,
          width: 300,
        };
      case 'workspaces-navigation':
        return {
          contents: <WorkspacesNavSheet onItemSelected={onRequestClose} />,
          width: 300,
        };
      default:
        return {
          contents: null,
          width: 300,
        };
    }
  }, [sheetId, onRequestClose]);

  const sheetOffset = useGetPrimaryNavBaseWidth();

  return (
    <ErrorBoundary>
      <WorkspacesSettingsContext>
        <Sheet
          id={APP_SHEET_ID}
          isShowing={Boolean(sheetId)}
          margin={isWebClient ? undefined : 0}
          offset={isWebClient ? sheetOffset : 0}
          offsetContainerStyles={
            sheetId !== 'project-switcher'
              ? NON_PROJECT_SHEET_STYLE
              : OFFSET_CONTAINER_STYLES
          }
          onRequestClose={onRequestClose}
          shouldUsePortal={false}
          size={width}
          transitionDelayOpen={isMobile ? PRIMARY_NAV_DURATION / 1000 : 0}
        >
          {contents}
        </Sheet>
      </WorkspacesSettingsContext>
    </ErrorBoundary>
  );
};
